import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Navigate } from "react-router-dom";
import { ExternalLink, LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { loginSchema, type LoginInput } from "@normax-performance/shared-types";
import { useAuth } from "../../app/providers/AuthProvider";
import { BrandLogo, Button, Input } from "../../shared-ui/primitives";
import { SUPPORT_EMAIL } from "../../shared-ui/support";
import normaxDarkPanelPerformance from "../../assets/brand/normax-dark-panel-performance.svg";
import "./LoginPage.css";

export function LoginPage() {
  const { t } = useTranslation("auth");
  const { user, isLoading, login, loginError, isLoggingIn } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  if (!isLoading && user) {
    return <Navigate to="/pregled" replace />;
  }

  return (
    <div className="nx-login">
      <div className="nx-login__visual">
        {/* normax-dark-panel-performance.svg (own dark backing panel, WHITE
            wordmark text) — deliberately NOT normax-logo-performance.svg
            (BrandLogo) here: that variant's wordmark text is dark
            (#06172A-based gradient) and would be unreadable against this
            panel's own dark gradient background. */}
        <img
          className="nx-login__visual-logo"
          src={normaxDarkPanelPerformance}
          alt="NormaX Performance Intelligence"
        />
        <p className="nx-login__quote">{t("login.quote")}</p>
        <p className="nx-login__caption">{t("login.caption")}</p>
      </div>
      <div className="nx-login__form-side">
        <form
          className="nx-login__card"
          onSubmit={handleSubmit(async (values) => {
            await login(values.companyCode, values.email, values.password).catch(() => undefined);
          })}
        >
          <BrandLogo size="md" className="nx-login__card-logo" />
          <h1 className="nx-login__title">{t("login.title")}</h1>
          <p className="nx-login__lede">{t("login.lede")}</p>

          <Input
            label={t("login.companyCodeLabel")}
            type="text"
            autoComplete="organization"
            required
            error={errors.companyCode?.message}
            {...register("companyCode")}
          />
          <Input
            label={t("login.emailLabel")}
            type="email"
            autoComplete="username"
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <Input
            label={t("login.passwordLabel")}
            type="password"
            autoComplete="current-password"
            required
            error={errors.password?.message}
            {...register("password")}
          />

          {loginError ? (
            <p className="nx-login__error" role="alert">
              {loginError}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="lg" loading={isLoggingIn}>
            <LogIn size={16} />
            {t("login.submit")}
          </Button>
          <p className="nx-login__footnote">{t("login.footnote")}</p>
          <div className="nx-login__links">
            <span className="nx-login__support">
              {t("login.supportLabel")}: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </span>
            <a
              className="nx-login__site-link"
              href="https://normax.rs/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t("login.siteLinkLabel")} (opens in a new tab)`}
            >
              {t("login.siteLinkLabel")}
              <ExternalLink size={12} aria-hidden="true" />
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
